<template>
    <div v-if="isVisible" class="launcher">
        <div aria-live="polite" aria-atomic="true">
            <BaseNotification v-if="statusMessage" theme="info" :text="statusMessage" class="status" @dismiss="statusMessage = null" />
        </div>

        <BasePopover v-model:open="popoverOpen" as-child modal side="top" align="end" :side-offset="8" :collision-padding="16" content-class="w-80 max-w-[calc(100vw-2rem)] p-4">
            <template #trigger>
                <BaseButton theme="neutral" class="trigger" title="Demo tools" aria-label="Open demo tools">
                    <i class="bi bi-magic" aria-hidden="true"></i>
                </BaseButton>
            </template>
            <template #content>
                <div class="flex flex-col gap-4">
                    <template v-if="panel === 'home'">
                        <h2 class="body-small-bold text-valence-stone-900">Demo tools</h2>

                        <BaseButton theme="neutral" size="sm" class="tool-row" @click="panel = 'onboarding'">
                            <i class="bi bi-signpost-split shrink-0" aria-hidden="true"></i>
                            <span class="grow truncate text-left">Onboarding tools</span>
                            <i class="bi bi-chevron-right shrink-0" aria-hidden="true"></i>
                        </BaseButton>
                    </template>

                    <template v-else>
                        <div>
                            <button type="button" class="back" @click="panel = 'home'">
                                <i class="bi bi-chevron-left" aria-hidden="true"></i>
                                Demo tools
                            </button>
                            <h2 class="body-small-bold mt-2 text-valence-stone-900">Onboarding tools</h2>
                        </div>

                        <div class="flex flex-col gap-2">
                            <BaseButton theme="neutral" size="sm" class="tool-row" :disabled="busy" @click="startContextOnboarding">
                                <i class="bi bi-play-circle shrink-0" aria-hidden="true"></i>
                                <span class="grow truncate text-left">Start context onboarding</span>
                                <i v-if="busy" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
                            </BaseButton>
                            <p class="details text-valence-stone-500">
                                Takes you to the first screen. Turns on the flag and its three prerequisites, clears your onboarding state, and disconnects your calendar so the
                                connect step is a real ask again.
                            </p>
                        </div>

                        <div class="fill-section">
                            <h3 class="body-small-bold text-valence-stone-900">Coaching setup (private profile)</h3>

                            <BaseButton theme="neutral" size="sm" class="tool-row" :disabled="busy" @click="resetSetup([])">
                                <i class="bi bi-arrow-counterclockwise shrink-0" aria-hidden="true"></i>
                                <span class="grow truncate text-left">Reset all four asks</span>
                                <i v-if="busy" class="bi bi-arrow-repeat shrink-0 spin" aria-hidden="true"></i>
                            </BaseButton>

                            <BaseButton
                                v-for="ask in SETUP_ASKS"
                                :key="ask.id"
                                theme="neutral"
                                size="sm"
                                class="tool-row"
                                :disabled="busy"
                                :aria-label="`Clear ${ask.label}, so that ask is outstanding again`"
                                @click="resetSetup([ask.id])">
                                <i class="bi bi-eraser shrink-0" aria-hidden="true"></i>
                                <span class="grow truncate text-left">Clear {{ ask.label }}</span>
                            </BaseButton>

                            <p class="details text-valence-stone-500">
                                Deletes what each ask produces, so it reads as outstanding again, and rebuilds the run. Nothing is seeded — an ask goes back to done by doing it,
                                which is the only way what you then review is the real thing.
                            </p>
                        </div>

                        <!-- Only while the Perspectives step is the one on screen: elsewhere
                             there is no form for it to act on. -->
                        <div v-if="onboardingDevToolsStore.canFillAssessment" class="fill-section">
                            <h3 class="body-small-bold text-valence-stone-900">On this step</h3>
                            <BaseButton theme="neutral" size="sm" class="tool-row" aria-label="Fill every unanswered Perspectives question" @click="fillAssessment">
                                <i class="bi bi-shuffle shrink-0" aria-hidden="true"></i>
                                <span class="grow truncate text-left">Fill remaining answers</span>
                                <span class="seed-tag shrink-0" title="Fills every unanswered question server-side and skips to the last page">synthetic</span>
                            </BaseButton>
                        </div>
                    </template>

                    <p v-if="inlineError" class="details text-status-error" role="alert">{{ inlineError }}</p>
                </div>
            </template>
        </BasePopover>
    </div>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePopover from "~vue/components/BasePopover.vue";
import type { SetupStepId } from "~vue/ProfileSetup/types";
import { onboardingDevToolsStore, requestAssessmentFill } from "~vue/stores/onboardingDevToolsStore";
import { computed, inject, type Ref, ref, watch } from "vue";

/**
 * Staff-only launcher for the demo tools.
 *
 * The endpoints behind it are registered only in development, staging and test,
 * so in production this renders nothing and there is no route to call. Mirrors
 * `pages/reflections/AssessmentDemoTools.vue`.
 *
 * Two levels rather than one flat list: this is where tooling for other areas
 * will land, and a single list would grow into an unreadable column of buttons
 * with nothing saying which feature each one belongs to.
 */

type Panel = "home" | "onboarding";

const userDetails = inject<Ref<UserDetails>>("user_details");
const popoverOpen = ref(false);
const panel = ref<Panel>("home");
const busy = ref(false);
const inlineError = ref<string | null>(null);
const statusMessage = ref<string | null>(null);

// `immediate: false` means the request only fires on `execute()`; awaiting the
// chain alone resolves without ever hitting the network.
const {
    execute: executeStart,
    data: startData,
    error: startError,
    statusCode,
} = useFetch("/onboarding/calendar-first/dev/start/", { immediate: false }).post({}).json<{ redirect_url: string; notes: string[] }>();

const SETUP_ASKS: { id: SetupStepId; label: string }[] = [
    { id: "identity", label: "name and role" },
    { id: "calendar", label: "calendar" },
    { id: "perspective", label: "perspective assessment" },
    { id: "publish", label: "collaboration profile" },
];

const resetBody = ref<{ steps: SetupStepId[] }>({ steps: [] });
const {
    execute: executeReset,
    error: resetError,
    statusCode: resetStatus,
} = useFetch("/onboarding/calendar-first/dev/setup/reset/", { immediate: false }).post(resetBody).json<{ cleared: string[]; notes: string[] }>();

const isVisible = computed(() => userDetails?.value.is_staff === true && userDetails?.value.calendar_first_onboarding_tools_enabled === true);

/* Reopening lands on the top level: which panel you left it on is not state
   worth remembering, and coming back to a sub-panel hides the other tools. */
watch(popoverOpen, (open) => {
    if (!open) {
        panel.value = "home";
        inlineError.value = null;
    }
});

function fillAssessment(): void {
    requestAssessmentFill();
    popoverOpen.value = false;
}

/**
 * An empty selection resets all four asks. Reload to refresh Inertia setup props and discard any panel
 * displaying an ask invalidated by the reset.
 */
async function resetSetup(steps: SetupStepId[]): Promise<void> {
    if (busy.value) {
        return;
    }
    busy.value = true;
    inlineError.value = null;
    resetBody.value = { steps };

    await executeReset();
    busy.value = false;

    if (resetError.value) {
        inlineError.value =
            resetStatus.value === 403 || resetStatus.value === 404
                ? "These tools are staff-only, and only exist outside production."
                : resetError.value.message || "Could not reset the coaching setup.";
        return;
    }

    globalThis.location.reload();
}

async function startContextOnboarding(): Promise<void> {
    if (busy.value) {
        return;
    }
    busy.value = true;
    inlineError.value = null;
    statusMessage.value = null;

    await executeStart();
    busy.value = false;

    const redirectUrl = startData.value?.redirect_url;
    if (startError.value || !redirectUrl) {
        inlineError.value =
            statusCode.value === 403 || statusCode.value === 404
                ? "These tools are staff-only, and only exist outside production."
                : startError.value?.message || "Could not set up the flow.";
        return;
    }

    globalThis.location.href = redirectUrl;
}
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    /* --mobile-nav-total-height is set on :root at every width, so the md
       override has to be an explicit media query that comes after it. */
    /*
     * Keep the launcher above the setup dialog at z-30 so debugging controls remain accessible during
     * setup.
     */
    .launcher {
        @apply fixed right-4 z-40 flex max-w-[calc(100vw-2rem)] flex-col items-end gap-3 md:right-6;
        bottom: calc(var(--mobile-nav-total-height, 0px) + 1rem);

        @media (width >= theme(--breakpoint-md)) {
            bottom: 1.5rem;
        }
    }

    .status {
        @apply max-w-sm shadow-lg;
    }

    .trigger {
        @apply inline-flex size-10 items-center justify-center rounded-full p-0 opacity-70 shadow-lg transition-opacity hover:opacity-100 focus-visible:opacity-100;
    }

    .tool-row {
        @apply flex w-full items-center gap-2 overflow-hidden text-left;
    }

    .back {
        @apply details inline-flex cursor-pointer items-center gap-1 text-valence-stone-500 hover:text-valence-stone-900;
        @apply focus-visible:rounded-sm focus-visible:ring-2 focus-visible:ring-valence-primary-500 focus-visible:outline-none;
    }

    .fill-section {
        @apply flex flex-col gap-2 border-t border-valence-cream-200 pt-3;
    }

    .seed-tag {
        @apply details rounded-full bg-valence-cream-200 px-2 py-0.5 text-valence-stone-500;
    }

    .spin {
        animation: dev-tools-spin 1s linear infinite;
    }
}

@keyframes dev-tools-spin {
    to {
        transform: rotate(360deg);
    }
}

@media (prefers-reduced-motion: reduce) {
    .spin {
        animation: none;
    }
}
</style>
